// @ts-check
// Navegação por teclado no canvas do fluxo (spec 2.0.0, fluxos.md seção 20.5, "Teclado e acessibilidade"; contrato 09
// de fluxos, seção 17). Uma parada de Tab no canvas; dentro dela as setas andam pela árvore: direita para o próximo
// passo (numa divisão, o primeiro do primeiro caminho), esquerda para o anterior (do primeiro passo de um caminho, a
// divisão), baixo e cima trocam de caminho na divisão mais próxima, Home vai ao cartão de entrada e End ao último passo
// do caminho atual. Caminho vazio tem foco no "+" dele. Função pura: devolve o próximo alvo.
import { BRANCH_KEYS_BY_TYPE, indexFlowNodes } from '../../../shared/flows/flow-definition.js';

/**
 * @typedef {import('../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../shared/flows/flow-definition.js').IndexedFlowNode} IndexedFlowNode
 */

/** Alvo do cartão de entrada. */
export const FLOW_ENTRY_FOCUS = 'entry';
const EMPTY_BRANCH_PREFIX = 'empty:';

/** Alvo do "+" de um caminho vazio (ou da raiz sem passos). @param {string | null} parentId @param {string | null} branchKey */
export const emptyListFocus = (parentId, branchKey) => `${EMPTY_BRANCH_PREFIX}${parentId ?? 'root'}:${branchKey ?? ''}`;

/** @param {string} focus @returns {{ parentId: string | null, branchKey: string | null } | null} */
export function parseEmptyListFocus(focus) {
  if (!focus.startsWith(EMPTY_BRANCH_PREFIX)) return null;
  const [parent, branch] = focus.slice(EMPTY_BRANCH_PREFIX.length).split(':');
  return { parentId: parent === 'root' ? null : parent, branchKey: branch || null };
}

/** @param {IndexedFlowNode['node']} node */
const branchKeysOf = (node) => (node.branches ? [...(BRANCH_KEYS_BY_TYPE[/** @type {keyof typeof BRANCH_KEYS_BY_TYPE} */ (node.type)] ?? Object.keys(node.branches))] : []);

/**
 * Primeiro alvo de um caminho: o primeiro passo, ou o "+" do caminho vazio.
 * @param {IndexedFlowNode['node']} split @param {string} branchKey
 */
function firstOfBranch(split, branchKey) {
  const first = split.branches?.[branchKey]?.[0];
  return first ? first.id : emptyListFocus(split.id, branchKey);
}

/**
 * Próximo alvo do foco para a tecla, ou o mesmo quando não há para onde ir.
 * @param {FlowDefinition} definition @param {string} focus id de passo, FLOW_ENTRY_FOCUS ou o alvo de um caminho vazio
 * @param {'ArrowRight' | 'ArrowLeft' | 'ArrowDown' | 'ArrowUp' | 'Home' | 'End'} key
 * @returns {string}
 */
export function moveFlowFocus(definition, focus, key) {
  const index = indexFlowNodes(definition);
  if (key === 'Home') return FLOW_ENTRY_FOCUS;
  const rootFirst = definition.nodes[0]?.id ?? emptyListFocus(null, null);
  if (focus === FLOW_ENTRY_FOCUS) {
    if (key === 'ArrowRight') return rootFirst;
    if (key === 'End') return lastOfList(definition.nodes) ?? FLOW_ENTRY_FOCUS;
    return focus;
  }
  const empty = parseEmptyListFocus(focus);
  if (empty) {
    if (!empty.parentId) return key === 'ArrowLeft' ? FLOW_ENTRY_FOCUS : focus;
    const split = index.get(empty.parentId)?.node;
    if (!split) return FLOW_ENTRY_FOCUS;
    if (key === 'ArrowLeft') return split.id;
    if (key === 'ArrowDown' || key === 'ArrowUp') return siblingBranch(split, /** @type {string} */ (empty.branchKey), key === 'ArrowDown' ? 1 : -1) ?? focus;
    return focus;
  }
  const entry = index.get(focus);
  if (!entry) return FLOW_ENTRY_FOCUS;
  const { node, siblings } = entry;
  switch (key) {
    case 'ArrowRight': {
      const keys = branchKeysOf(node);
      if (keys.length > 0) return firstOfBranch(node, keys[0]);
      return siblings[entry.index + 1]?.id ?? focus;
    }
    case 'ArrowLeft':
      if (entry.index > 0) return siblings[entry.index - 1].id;
      return entry.parentId ?? FLOW_ENTRY_FOCUS;
    case 'ArrowDown':
    case 'ArrowUp': {
      const step = key === 'ArrowDown' ? 1 : -1;
      // na própria divisão: o primeiro caminho fica em cima, o segundo embaixo
      const keys = branchKeysOf(node);
      if (keys.length > 0) return firstOfBranch(node, keys[step > 0 ? keys.length - 1 : 0]);
      if (!entry.parentId || !entry.branchKey) return focus;
      const split = index.get(entry.parentId)?.node;
      return split ? siblingBranch(split, entry.branchKey, step) ?? focus : focus;
    }
    case 'End':
      return lastOfList(siblings) ?? focus;
    default:
      return focus;
  }
}

/**
 * Primeiro alvo do caminho vizinho na divisão.
 * @param {IndexedFlowNode['node']} split @param {string} branchKey @param {number} step
 */
function siblingBranch(split, branchKey, step) {
  const keys = branchKeysOf(split);
  const position = keys.indexOf(branchKey) + step;
  if (position < 0 || position >= keys.length) return null;
  return firstOfBranch(split, keys[position]);
}

/** @param {IndexedFlowNode['node'][]} list */
const lastOfList = (list) => list[list.length - 1]?.id ?? null;

/**
 * Nível (aria-level) de um passo: divisões no caminho mais um; o cartão de entrada é o nível 1.
 * @param {FlowDefinition} definition @param {string} nodeId
 */
export function flowTreeLevel(definition, nodeId) {
  const entry = indexFlowNodes(definition).get(nodeId);
  return entry ? entry.depth + 1 : 1;
}

/**
 * Número do passo na leitura do canvas (pré-ordem: cada passo antes dos caminhos dele), para "Passo 3: Esperar...".
 * @param {FlowDefinition} definition
 * @returns {Map<string, number>}
 */
export function flowStepNumbers(definition) {
  /** @type {Map<string, number>} */
  const numbers = new Map();
  for (const id of indexFlowNodes(definition).keys()) numbers.set(id, numbers.size + 1);
  return numbers;
}
